"use client";

import { c as _c } from "react/compiler-runtime";
import { EllipsisVerticalIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { memo, useEffect, useRef, useState } from "react";
import { useClickAway } from "react-use";
import { getConnectionProfileIndicator } from "@/components/auth/connection-state-indicator";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import HeaderUserConnect from "@/components/header/user/HeaderUserConnect";
import HeaderUserProxyDropdown from "@/components/header/user/proxy/HeaderUserProxyDropdown";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import UserLevel from "@/components/user/utils/level/UserLevel";
import { DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP } from "@/constants/constants";
import { isAutoGeneratedHandle, isEthereumAddress } from "@/helpers/AllowlistToolHelpers";
import { useIdentity } from "@/hooks/useIdentity";
interface WebSidebarUserProps {
  readonly isCollapsed: boolean;
  readonly profile: any;
}
function WebSidebarUser(t0) {
  const $ = _c(73);
  const {
    isCollapsed,
    profile: parentProfile
  } = t0;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const {
    address,
    seizeConnect,
    isAuthenticated,
    isConnected,
    connectedAccounts,
    connectedAccountUnreadNotifications,
    seizeSwitchConnectedAccount
  } = useSeizeConnectContext();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const profileClickTimeoutRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (profileClickTimeoutRef.current) {
        clearTimeout(profileClickTimeoutRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== showUserMenu) {
    t3 = event => {
      if (showUserMenu && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    $[2] = showUserMenu;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useClickAway(dropdownRef, t3);
  const t4 = address || "";
  let t5;
  if ($[4] !== t4) {
    t5 = {
      handleOrWallet: t4,
      initialProfile: null
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    profile: localProfile,
    isLoading
  } = useIdentity(t5);
  const profile = parentProfile || localProfile;
  let t6;
  if ($[6] !== profile) {
    t6 = profile?.pfp ? resolveIpfsUrlSync(profile.pfp) : null;
    $[6] = profile;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const resolvedPfp = t6;
  const [avatarSrc, setAvatarSrc] = useState(resolvedPfp ?? DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
  const isResolvedAvatar = !!resolvedPfp && avatarSrc === resolvedPfp;
  let t7;
  let t8;
  if ($[8] !== resolvedPfp) {
    t7 = () => {
      setAvatarSrc(resolvedPfp ?? DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
    };
    t8 = [resolvedPfp];
    $[8] = resolvedPfp;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  if (!address) {
    let t9;
    if ($[11] !== isCollapsed || $[12] !== seizeConnect) {
      t9 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3">{isCollapsed ? <button className="tw-block tw-h-12 tw-w-full tw-cursor-pointer tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-text-base tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500 focus-visible:tw-ring-offset-2 active:tw-bg-transparent desktop-hover:hover:tw-text-white" onClick={() => seizeConnect()} title="Connect Wallet" data-tooltip-id="sidebar-tooltip" data-tooltip-content="Connect Wallet"><div className="tw-flex tw-h-full tw-w-full tw-items-center"><div className="tw-flex tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center"><UserCircleIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></div></div></button> : <div className="tw-mt-2 tw-w-full tw-px-2"><div className="[&>button]:tw-w-full"><HeaderUserConnect /></div></div>}</div>;
      $[11] = isCollapsed;
      $[12] = seizeConnect;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (isLoading || !profile) {
    const t9 = `tw-flex tw-w-full tw-items-center tw-rounded-xl tw-px-2 tw-py-2 ${isCollapsed ? "tw-justify-center" : "tw-justify-start tw-gap-x-3"}`;
    let t10;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-animate-pulse tw-rounded-lg tw-bg-iron-800" />;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== isCollapsed) {
      t11 = !isCollapsed && <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-2"><div className="tw-h-4 tw-w-24 tw-animate-pulse tw-rounded tw-bg-iron-800" /><div className="tw-h-3 tw-w-16 tw-animate-pulse tw-rounded tw-bg-iron-800" /></div>;
      $[15] = isCollapsed;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== t11 || $[18] !== t9) {
      t12 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3"><div className={t9}>{t10}{t11}</div></div>;
      $[17] = t11;
      $[18] = t9;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    return t12;
  }
  const displayHandle = profile.handle || address || "";
  let t9;
  if ($[20] !== address) {
    t9 = address && (isEthereumAddress(address) || isAutoGeneratedHandle(address));
    $[20] = address;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const shouldTruncate = t9;
  let t10;
  if ($[22] !== isAuthenticated || $[23] !== isConnected) {
    t10 = getConnectionProfileIndicator({
      isAuthenticated,
      isConnected
    });
    $[22] = isAuthenticated;
    $[23] = isConnected;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const connectionIndicator = t10;
  let t11;
  if ($[25] !== connectedAccountUnreadNotifications || $[26] !== connectedAccounts) {
    let t12;
    if ($[28] !== connectedAccountUnreadNotifications) {
      t12 = account => !account.isActive && (connectedAccountUnreadNotifications[account.address.toLowerCase()] ?? 0) > 0;
      $[28] = connectedAccountUnreadNotifications;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    t11 = connectedAccounts.some(t12);
    $[25] = connectedAccountUnreadNotifications;
    $[26] = connectedAccounts;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const hasUnreadOnOtherConnectedProfiles = t11;
  let t12;
  if ($[30] !== connectedAccounts || $[31] !== seizeSwitchConnectedAccount) {
    t12 = () => {
      if (connectedAccounts.length < 2) {
        return false;
      }
      const activeIndex = connectedAccounts.findIndex(_temp);
      const currentIndex = Math.max(activeIndex, 0);
      const nextAccount = connectedAccounts[(currentIndex + 1) % connectedAccounts.length];
      if (!nextAccount) {
        return false;
      }
      ;
      try {
        seizeSwitchConnectedAccount(nextAccount.address);
        return true;
      } catch (t13) {
        const error = t13;
        console.error("Failed to switch connected account", error);
        return false;
      }
    };
    $[30] = connectedAccounts;
    $[31] = seizeSwitchConnectedAccount;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const switchToNextConnectedAccount = t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setShowUserMenu(_temp2);
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const toggleUserMenu = t13;
  let t14;
  if ($[34] !== switchToNextConnectedAccount) {
    t14 = () => {
      if (profileClickTimeoutRef.current) {
        clearTimeout(profileClickTimeoutRef.current);
        profileClickTimeoutRef.current = null;
        const didSwitchAccount = switchToNextConnectedAccount();
        if (!didSwitchAccount) {
          toggleUserMenu();
        }
        return;
      }
      profileClickTimeoutRef.current = setTimeout(() => {
        profileClickTimeoutRef.current = null;
        toggleUserMenu();
      }, 280);
    };
    $[34] = switchToNextConnectedAccount;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const onProfileActivate = t14;
  const t15 = `tw-group/user tw-mt-1 tw-w-full tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors tw-duration-200 ${isCollapsed ? "" : "desktop-hover:hover:tw-bg-iron-900"}`;
  const t16 = `tw-flex tw-w-full tw-items-center ${isCollapsed ? "" : "tw-gap-x-2"}`;
  const t17 = `tw-relative tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl ${connectionIndicator.avatarClassName}`;
  const t18 = displayHandle || "Connected account";
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_0 => {
      if (event_0.currentTarget.src.endsWith(DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP)) {
        return;
      }
      setAvatarSrc(DEFAULT_CONNECTED_PROFILE_FALLBACK_PFP);
    };
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = `tw-absolute tw-inset-0 tw-block tw-h-full tw-w-full tw-rounded-xl tw-bg-iron-900 ${isResolvedAvatar ? "tw-object-contain" : "tw-object-cover tw-grayscale"}`;
  let t21;
  if ($[37] !== avatarSrc || $[38] !== t18 || $[39] !== t20) {
    t21 = <img src={avatarSrc} alt={t18} onError={t19} className={t20} />;
    $[37] = avatarSrc;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== connectionIndicator.overlayClassName) {
    t22 = connectionIndicator.overlayClassName && <div className={`tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-xl ${connectionIndicator.overlayClassName}`} />;
    $[41] = connectionIndicator.overlayClassName;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== connectionIndicator.title || $[44] !== t17 || $[45] !== t21 || $[46] !== t22) {
    t23 = <div className={t17} title={connectionIndicator.title}>{t21}{t22}</div>;
    $[43] = connectionIndicator.title;
    $[44] = t17;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== hasUnreadOnOtherConnectedProfiles) {
    t24 = hasUnreadOnOtherConnectedProfiles && <div className="tw-absolute tw-right-[-6px] tw-top-[-6px] tw-size-3 tw-rounded-full tw-bg-indigo-500 tw-shadow-sm" aria-hidden="true" />;
    $[48] = hasUnreadOnOtherConnectedProfiles;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t23 || $[51] !== t24) {
    t25 = <div className="tw-relative tw-h-10 tw-w-10 tw-flex-shrink-0">{t23}{t24}</div>;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== displayHandle || $[54] !== isCollapsed || $[55] !== profile.level || $[56] !== shouldTruncate) {
    t26 = !isCollapsed && <><div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-justify-between tw-whitespace-nowrap"><div className={`-tw-mt-0.5 tw-mb-1 tw-w-full tw-overflow-hidden tw-text-left tw-text-md tw-font-medium tw-leading-tight tw-text-white ${shouldTruncate ? "tw-max-w-[8rem] tw-truncate" : ""}`}>{displayHandle}</div><UserLevel level={profile.level} size="xxs" asSpan={true} /></div><EllipsisVerticalIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-400 group-hover/user:tw-text-white" /></>;
    $[53] = displayHandle;
    $[54] = isCollapsed;
    $[55] = profile.level;
    $[56] = shouldTruncate;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t16 || $[59] !== t25 || $[60] !== t26) {
    t27 = <div className={t16}>{t25}{t26}</div>;
    $[58] = t16;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== onProfileActivate || $[63] !== showUserMenu || $[64] !== t15 || $[65] !== t27) {
    t28 = <button ref={buttonRef} onClick={onProfileActivate} className={t15} aria-label="Open user menu" aria-expanded={showUserMenu} aria-controls="user-menu">{t27}</button>;
    $[62] = onProfileActivate;
    $[63] = showUserMenu;
    $[64] = t15;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== profile || $[68] !== showUserMenu) {
    t29 = showUserMenu && profile && <div ref={dropdownRef}><HeaderUserProxyDropdown profile={profile} isOpen={showUserMenu} onClose={() => setShowUserMenu(false)} /></div>;
    $[67] = profile;
    $[68] = showUserMenu;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t28 || $[71] !== t29) {
    t30 = <div className="tw-mt-auto tw-relative tw-w-full tw-group tw-pb-2 tw-px-3">{t28}{t29}</div>;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  return t30;
}
function _temp2(current) {
  return !current;
}
function _temp(account_0) {
  return account_0.isActive;
}
export default memo(WebSidebarUser);